import React, { Fragment, useContext } from "react";
import clsx from "clsx";

import ArrowRightIcon from "~/icons/ArrowRightIcon";
import MinusIcon from "~/icons/MinusIcon";
import PlusIcon from "~/icons/PlusIcon";
import Button from "~/components/Button/Button";
import Checkbox from "~/components/Checkbox/Checkbox";
import RadioButton from "~/components/RadioButton/RadioButton";

import {
  cartIsValid,
  generateChildItem,
  showWhenEmpty,
  withCorrectItemShouldHide,
} from "~/helpers/cartHelpers";

import styles from "./ModifierOptions.module.css";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import {
  ProductModalModifierFontSize,
  ProductModalModifierHeaderFontSize,
} from "~/theme/models/themeModels";
import { CartLineItem, OrderSource } from "~/entities/orderEntities";
import { Product, ProductChild } from "~/entities/productEntities";
import sum from "lodash/sum";

type ModifierOptionsProps = {
  // subView is true if this ModifierOptions is nested in another ModifierOptions
  subView?: boolean;
  cart: CartLineItem;
  setCart: (newCart: CartLineItem) => void;
  viewOptionsForChild: (childIdPath: string[]) => void;
  showError: boolean;
  orderSource: OrderSource;
};

const ModifierOptions: React.FC<ModifierOptionsProps> = ({
  subView = false,
  cart,
  setCart,
  viewOptionsForChild,
  showError,
  orderSource,
}) => {
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  const product: Product = cart.product;

  const getChildCount = (productId: string) => {
    return sum(
      cart.children
        .filter((x) => x.product.code === productId)
        .map((x) => x.qty),
    );
  };
  const totalChildrenCount = sum(cart.children.map((x) => x.qty));

  const addOne = (prodChild: ProductChild) => {
    // Can't add beyond maxQty
    if (
      prodChild.maxQty != null &&
      prodChild.maxQty <= getChildCount(prodChild.product.code)
    ) {
      return;
    }

    // Can't add beyond max children
    if (product.maxChildren && totalChildrenCount >= product.maxChildren) {
      if (product.maxChildren === 1) {
        setCart({
          ...cart,
          children: [generateChildItem(prodChild, 1)],
        });
      }
      return;
    }

    const target = cart.children
      .slice()
      .reverse()
      .find((x) => x.product.code === prodChild.product.code);

    // If there is an existing line with qty 0, replace it with a new line (this adds children back if needed)
    if (target && target.qty === 0) {
      setCart({
        ...cart,
        children: cart.children.map((x) =>
          x !== target ? x : generateChildItem(prodChild, 1),
        ),
      });
      return;
    }

    // If product has no children, increase qty of an existing item if possible
    if (!prodChild.product.children?.length && target) {
      setCart({
        ...cart,
        children: cart.children.map((x) =>
          x !== target
            ? x
            : withCorrectItemShouldHide({ ...x, qty: x.qty + 1 }, prodChild),
        ),
      });
      return;
    }

    // Add a new child
    // Add it in order according to the order of the product children
    // This is so that the list of modifiers doesn't change order after the api responds, since the api will sort
    // according to the product child list.

    // Find the index of the last item earlier or the same in the product order

    let insertIndex = 0;

    // Get index of this product child
    const prodChildIndex = product.children.findIndex(
      (x) => x.productCode === prodChild.productCode,
    );

    // Loop through all the product children up to and including this one
    for (let i = 0; i <= prodChildIndex; i++) {
      // Increment the insert index whilst it points to this product child
      while (
        insertIndex < cart.children.length &&
        cart.children[insertIndex].productCode ===
          product.children[i].productCode
      ) {
        insertIndex++;
      }
    }

    setCart({
      ...cart,
      children: [
        ...cart.children.slice(0, insertIndex),
        generateChildItem(prodChild, 1),
        ...cart.children.slice(insertIndex),
      ],
    });
  };

  const removeOne = (prodChild: ProductChild) => {
    if (getChildCount(prodChild.product.code) <= (prodChild.minQty ?? 0)) {
      return;
    }

    const target = cart.children
      .slice()
      .reverse()
      .find((x) => x.product.code === prodChild.product.code);

    if (!target) {
      return;
    }

    if (target.qty > 1) {
      // For items with qty > 1, just reduce the qty
      setCart({
        ...cart,
        children: cart.children.map((x) =>
          x !== target
            ? x
            : withCorrectItemShouldHide({ ...x, qty: x.qty - 1 }, prodChild),
        ),
      });
    } else {
      // Special rules for when we require an empty item (and no other child fulfil this)
      if (
        showWhenEmpty(prodChild) &&
        cart.children.every(
          (x) => x.product.code !== prodChild.product.code || x === target,
        )
      ) {
        // In this case set qty 0 and remove all children
        setCart({
          ...cart,
          children: cart.children.map((x) =>
            x !== target ? x : { ...x, qty: 0, children: [], hide: false },
          ),
        });
      } else {
        // We have qty 1 and don't require an empty item, so remove the item
        setCart({
          ...cart,
          children: cart.children.filter((x) => x !== target),
        });
      }
    }
  };

  const childShowCheckbox = (child: ProductChild) => {
    return child.maxQty === 1 || product.maxChildren === 1;
  };

  const productIdChecked = (child: ProductChild) => {
    return getChildCount(child.product.code) !== 0;
  };

  const childOnCheck = (child: ProductChild) => () => {
    if (getChildCount(child.product.code) === 0) {
      addOne(child);
    } else {
      removeOne(child);
    }
  };

  const getLabel = (required: boolean) => {
    let label = required ? "Required " : "";
    const minChildren = product.minChildren ?? 0;
    const maxChildren = product.maxChildren ?? 0;

    if (minChildren === 1 && minChildren === maxChildren) {
      label = `${label}(option)`;
    } else if (minChildren) {
      label = `${label}(min ${minChildren})`;
    } else if (maxChildren) {
      label = `${label}(max ${maxChildren})`;
    }

    return label;
  };

  const isError =
    showError && totalChildrenCount < (cart.product.minChildren || 0);

  const getChildOptions = (child: ProductChild) => {
    return cart.children.filter(
      (childCartItem) =>
        childCartItem.product.code === child.product.code &&
        childCartItem.product.children?.length &&
        childCartItem.qty,
    );
  };

  // Styles
  const headerBackgroundColor =
    template.productScreen.modifiers.headerBackgroundColour;
  const headerFontSize =
    ProductModalModifierHeaderFontSize[
      template.productScreen.modifiers.headerFontSize
    ];
  const headerFontColor = template.productScreen.modifiers.headerFontColour;
  const modifierFontSize =
    ProductModalModifierFontSize[template.productScreen.modifiers.fontSize];
  const modifierFontColor = template.productScreen.modifiers.fontColour;
  const modifierPriceColour = template.productScreen.modifiers.priceColour;

  return !product.children ? null : (
    <div>
      {!subView ? (
        // bar to show min / max selections at the top WITHOUT the product name as it is already displayed either on the left or as a header
        // It is only needed if we have min / max selections
        <>
          {(product.minChildren || product.maxChildren) &&
            product?.children.some(
              (child) => (child.minQty ?? 0) !== child.maxQty,
            ) && (
              <div
                className={styles.productOptionsHeader}
                style={{
                  backgroundColor: headerBackgroundColor,
                  color: headerFontColor,
                  fontSize: headerFontSize,
                }}
              >
                <div className={styles.productOptionsHeaderName} />
                <label
                  className={clsx(
                    styles.shadowLabel,
                    isError && styles.errorColor,
                  )}
                >
                  {getLabel((product.minChildren ?? 0) > 0)}
                </label>
              </div>
            )}
        </>
      ) : (
        // bar to show the product name and min / max selections
        <div
          className={styles.productOptionsHeader}
          style={{
            backgroundColor: headerBackgroundColor,
            color: headerFontColor,
            fontSize: headerFontSize,
          }}
        >
          <div
            className={clsx(
              styles.productOptionNameInfo,
              isError && styles.errorColor,
            )}
          >
            {
              // Show "No __" or "__" or "__ × _" depending
              cart.qty === 0
                ? `No\xa0${product.name}`
                : cart.qty === 1
                  ? product.name
                  : `${product.name}\xa0×\xa0${cart.qty}`
            }
          </div>
          <label
            className={clsx(styles.shadowLabel, isError && styles.errorColor)}
          >
            {getLabel((product.minChildren ?? 0) > 0)}
          </label>
        </div>
      )}
      <div
        className={styles.productChildren}
        style={{ fontSize: modifierFontSize, color: modifierFontColor }}
      >
        {product?.children.map((child: ProductChild) => (
          <Fragment key={child.product.code}>
            {
              // If minQty = maxQty just render all child cart items direct with no controls
              (child.minQty ?? 0) === child.maxQty ? (
                cart.children
                  .filter(
                    (childCartItem) =>
                      childCartItem.product.code === child.product.code,
                  )
                  .map((childCartItem) => (
                    <ModifierOptions
                      subView={true}
                      key={childCartItem.id}
                      cart={childCartItem}
                      setCart={(newCart) => {
                        setCart({
                          ...cart,
                          children: cart.children.map((x) =>
                            x.id === childCartItem.id ? newCart : x,
                          ),
                        });
                      }}
                      viewOptionsForChild={(pathToChild: string[]) => {
                        viewOptionsForChild(
                          [childCartItem.id].concat(...pathToChild),
                        );
                      }}
                      showError={showError}
                      orderSource={orderSource}
                    />
                  ))
              ) : (
                // Otherwise, if minQty != maxQty, then render controls for adding/removing items
                // Also show buttons for further configuring items where necessary
                <>
                  {/* SHOW CONTROL FOR ADDING / REMOVING */}
                  <div
                    key={child.product.code}
                    className={clsx(
                      styles.productOption,
                      !!childOnCheck(child) && styles.productOptionChecked,
                      !(
                        childShowCheckbox(child) &&
                        !productIdChecked(child) &&
                        child.product.soldOut
                      ) && styles.productOptionAvailable,
                      child.product.soldOut &&
                        productIdChecked(child) &&
                        styles.productOptionSelectedSoldOut,
                      getChildOptions(child).length &&
                        styles.productOptionBorderless,
                    )}
                    onClick={
                      childShowCheckbox(child) ? childOnCheck(child) : undefined
                    }
                  >
                    <div className={styles.productOptionDetails}>
                      <div className={styles.productOptionName}>
                        {child.product.name}
                        {child.product.soldOut && " (Sold Out)"}
                      </div>
                      {child.product.price !== 0 && (
                        <div
                          className={styles.price}
                          style={{ color: modifierPriceColour }}
                        >
                          {formatAmount(child.product.price ?? 0, true)}
                        </div>
                      )}
                    </div>
                    {childShowCheckbox(child) ? (
                      <>
                        {product.minChildren ? (
                          <div className={styles.radioButtonWrapper}>
                            <RadioButton
                              checked={productIdChecked(child)}
                              color={modifierFontColor}
                            />
                          </div>
                        ) : (
                          <div className={styles.checkboxWrapper}>
                            <Checkbox checked={productIdChecked(child)} />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className={styles.counterButtons}>
                          <Button
                            variant="counter-secondary"
                            onClick={() => {
                              removeOne(child);
                            }}
                            disabled={
                              getChildCount(child.product.code) <=
                              (child.minQty ?? 0)
                            }
                          >
                            <MinusIcon />
                          </Button>
                          <div className={styles.selectedCountLabel}>
                            {getChildCount(child.product.code)}
                          </div>
                          <Button
                            variant="counter-primary"
                            onClick={() => {
                              addOne(child);
                            }}
                            disabled={
                              child.product.soldOut ||
                              (child.maxQty != null &&
                                getChildCount(child.product.code) >=
                                  child.maxQty)
                            }
                          >
                            <PlusIcon />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                  {/* SHOW BUTTONS FOR MORE DETAILS */}
                  {getChildOptions(child).map((childCartItem, index) => (
                    <div
                      key={childCartItem.id}
                      className={clsx(
                        styles.productOptions,
                        showError &&
                          !cartIsValid(childCartItem) &&
                          styles.errorColor,
                        getChildOptions(child).length === index + 1 &&
                          styles.productOptionsLast,
                      )}
                      style={{
                        backgroundColor: headerBackgroundColor,
                        color: headerFontColor,
                        fontSize: headerFontSize,
                      }}
                      onClick={() => {
                        viewOptionsForChild([childCartItem.id]);
                      }}
                    >
                      <span>{childCartItem.product.name} options</span>
                      <ArrowRightIcon color={headerFontColor} />
                    </div>
                  ))}
                </>
              )
            }
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default ModifierOptions;
